import React from 'react';
import PropTypes from 'prop-types';
import { Alert, Button } from 'react-bootstrap';
import { OPEN_STATUSES } from '@/shared/constants/listings';
import RequestCard from './RequestCard';

/**
 * Every request on one listing, for the owner or an admin.
 *
 * Which actions a card offers is the listing's state, not the request's
 * alone: accepting is only possible while the listing is still open, and
 * closing the exchange only once it is claimed. Gating on the request status
 * alone would offer Accept on a listing already promised to someone else.
 *
 * Declining is the exception. `declineRequest` has no listing check, so a
 * listing that expired with requests standing against it can still be
 * cleared down, and withholding Decline there would leave its cards with no
 * action at all.
 */
function RequestsPanel({
    requests,
    listing,
    error = '',
    onAccept,
    onDecline,
    onComplete,
    onRelease,
}) {
    const listingOpen = OPEN_STATUSES.includes(listing.status);
    const claimed = listing.status === 'claimed';
    // Sort is stable, so the server's newest-first order survives underneath
    // the accepted request being pulled to the front.
    const ordered = claimed
        ? [...requests].sort(
              (a, b) =>
                  Number(b.status === 'accepted') -
                  Number(a.status === 'accepted')
          )
        : requests;

    const actionsFor = (request) => {
        const name = request.requester?.name;
        if (request.status === 'open') {
            return (
                <>
                    {listingOpen && (
                        <Button
                            size="sm"
                            aria-label={`Accept request from ${name}`}
                            onClick={() => onAccept(request._id)}
                        >
                            Accept
                        </Button>
                    )}
                    <Button
                        size="sm"
                        variant="outline-secondary"
                        aria-label={`Decline request from ${name}`}
                        onClick={() => onDecline(request._id)}
                    >
                        Decline
                    </Button>
                </>
            );
        }
        if (claimed && request.status === 'accepted') {
            return (
                <>
                    <Button size="sm" onClick={onComplete}>
                        Mark completed
                    </Button>
                    <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={onRelease}
                    >
                        Release claim
                    </Button>
                </>
            );
        }
        return null;
    };

    return (
        <section aria-label="Requests" className="mt-4">
            <h2 className="h5">Requests</h2>
            {error && <Alert variant="danger">{error}</Alert>}
            {!error && ordered.length === 0 && (
                <p className="text-muted">
                    No one has requested this item yet.
                </p>
            )}
            {ordered.map((request) => (
                <RequestCard key={request._id} request={request}>
                    {actionsFor(request)}
                </RequestCard>
            ))}
        </section>
    );
}

RequestsPanel.propTypes = {
    requests: PropTypes.array.isRequired,
    listing: PropTypes.object.isRequired,
    error: PropTypes.string,
    onAccept: PropTypes.func.isRequired,
    onDecline: PropTypes.func.isRequired,
    onComplete: PropTypes.func.isRequired,
    onRelease: PropTypes.func.isRequired,
};

export default RequestsPanel;
